import { z } from 'zod';
import { ApiClient, ApiScope } from './api-clients.js';
/**
 * Apps of third parties that firms connect (ADR-015). Registered first for testing - then they
 * connect to sandboxes only - and released for firms once Kanos has reviewed them. A firm
 * consents per app to a set of scopes; the app gets an installation of its own in the firm or
 * its sandbox - an API client that authenticates with the partner's key, never with a secret.
 */
/** Where an installation lives: the firm itself, or its sandbox with fictitious data (ADR-014). */
export const PartnerTarget = z.enum(['firm', 'sandbox']).meta({ id: 'PartnerTarget' });
/** An address the browser may be sent back to: https, or the loopback for development tools. */
const returnAddress = z.url().refine((value) => {
    const url = new URL(value);
    const loopback = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
    return (url.protocol === 'https:' || (url.protocol === 'http:' && loopback)) && !url.hash;
}, 'https (or the loopback), without fragment');
/** A partner app as a firm sees it before connecting it. */
export const PartnerApp = z
    .object({
    id: z.uuid(),
    name: z.string(),
    vendor: z.string(),
    website: z.url(),
    /** What a firm may grant the app at most. */
    scopes: z.array(ApiScope),
    /** Reviewed and released by Kanos; until then it connects to sandboxes only. */
    released: z.boolean(),
    /** The partner's data processing agreement to read, if Kanos holds a link to it. */
    dpaUrl: z.url().nullable(),
})
    .meta({ id: 'PartnerApp' });
/**
 * What the app asks for when it sends a firm's administrator to Kanos: the scopes
 * space-separated as in OAuth, the registered return address, its own `state`.
 */
export const PartnerConsentQuery = z
    .object({
    redirectUri: z.string().min(1).max(2000),
    scope: z.string().min(1).max(500),
    state: z.string().max(500).optional(),
})
    .meta({ id: 'PartnerConsentQuery' });
/** The consent a firm's administrator decides on. */
export const PartnerConsent = z
    .object({
    partnerApp: PartnerApp,
    /** The scopes asked for - each one the app may be granted. */
    scopes: z.array(ApiScope).min(1),
    /** The firm has an active installation of the app already. */
    installed: z.boolean(),
    /** The firm's open sandbox and whether the app is installed there; `null` without one. */
    sandbox: z.object({ installed: z.boolean() }).nullable(),
    /** Where the browser goes when the administrator declines (`error=access_denied`). */
    declineTo: z.url(),
})
    .meta({ id: 'PartnerConsent' });
export const PartnerAppParams = z
    .object({ partnerAppId: z.uuid() })
    .meta({ id: 'PartnerAppParams' });
export const PartnerInstallationInput = z
    .object({
    partnerAppId: z.uuid(),
    scopes: z.array(ApiScope).min(1).max(ApiScope.options.length),
    redirectUri: z.string().min(1).max(2000),
    state: z.string().max(500).optional(),
    /** The firm unless stated; an app in testing only into the sandbox. */
    target: PartnerTarget.optional(),
    /**
     * Into the firm (decided 2026-10-31, #40): the firm confirms it has concluded - or concludes -
     * a data processing agreement with the partner (Art. 28 GDPR) that binds it to
     * confidentiality (§ 62a StBerG). Without it, no connection; the sandbox holds fictitious
     * data only and needs none.
     */
    dpaConfirmed: z.boolean().optional(),
})
    .meta({ id: 'PartnerInstallationInput' });
/** The installation, and where the browser returns to the app with its client id. */
export const PartnerInstallationCreated = z
    .object({ client: ApiClient, redirectTo: z.url() })
    .meta({ id: 'PartnerInstallationCreated' });
/** A partner app as the platform registers it - for testing, in sandboxes (ADR-015 §1). */
export const PartnerAppRegistration = z.object({
    name: z.string().trim().min(1).max(100),
    vendor: z.string().trim().min(1).max(200),
    website: z.url({ protocol: /^https$/ }),
    scopes: z.array(ApiScope).min(1),
    redirectUris: z.array(returnAddress).min(1).max(10),
    jwksUri: z.url({ protocol: /^https$/ }),
});
/** The review that releases a partner app for firms (ADR-015 §1). */
export const PartnerAppRelease = z
    .object({
    /** The data processing agreement, e.g. its contract number. */
    dpaReference: z.string().trim().min(1).max(200),
    /** Where firms read the partner's agreement, shown on the consent page; may follow later. */
    dpaUrl: z
        .url({ protocol: /^https$/ })
        .max(2000)
        .optional(),
    /** Who reviewed it, by name or staff id - platform staff, not a firm's data. */
    reviewedBy: z.string().trim().min(1).max(200),
})
    .meta({ id: 'PartnerAppRelease' });
/** A partner app as the platform's administration sees it (ADR-016). */
export const PartnerAppAdmin = z
    .object({
    id: z.uuid(),
    name: z.string(),
    vendor: z.string(),
    website: z.url(),
    scopes: z.array(ApiScope),
    redirectUris: z.array(z.string()),
    jwksUri: z.string(),
    registeredAt: z.iso.datetime({ offset: true }),
    /** The review that released it; all `null` while it is in testing. */
    dpaReference: z.string().nullable(),
    dpaUrl: z.string().nullable(),
    reviewedAt: z.iso.datetime({ offset: true }).nullable(),
    reviewedBy: z.string().nullable(),
    suspendedAt: z.iso.datetime({ offset: true }).nullable(),
    suspendedReason: z.string().nullable(),
})
    .meta({ id: 'PartnerAppAdmin' });
export const PartnerAppAdminList = z.array(PartnerAppAdmin).meta({ id: 'PartnerAppAdminList' });
export const PartnerAppRegistrationInput = PartnerAppRegistration.meta({
    id: 'PartnerAppRegistration',
});
export const PartnerSuspensionInput = z
    .object({ reason: z.string().trim().min(3).max(500) })
    .meta({ id: 'PartnerSuspensionInput' });
/** How many installations the suspension revoked, across firms. */
export const PartnerSuspended = z
    .object({ revoked: z.number().int().min(0) })
    .meta({ id: 'PartnerSuspended' });
//# sourceMappingURL=partner-apps.js.map